import * as XLSX from 'xlsx';
import { Customization, DishInfo, CustomizationGroup } from '../types/customization';
import { DishService } from '../services/dishService';

const dishService = new DishService();

/**
 * Parse Excel file and convert it to Customization objects
 * @param file The Excel file to parse
 * @returns Array of Customization objects
 */
export const parseExcelToCustomizations = async (file: File): Promise<Customization[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet) as Record<string, any>[];

        // Fetch all dishes first to avoid multiple database calls
        const allDishes = await dishService.getAllDishes();
        const dishMap = new Map(allDishes.map(dish => [dish.code, dish]));

        const customizations: Customization[] = [];
        let currentCustomization: Customization | null = null;

        // Process each row
        for (const row of rows) {
          const name = row['Tên'] as string;
          
          // If row has a name, it's a new customization
          if (name) {
            // Save previous customization if exists
            if (currentCustomization) {
              customizations.push(currentCustomization);
            }

            // Create new customization
            currentCustomization = {
              name,
              city: (row['Thành phố'] as string) || '',
              dishCode: ((row['Mã món áp dụng'] as string) || '').split(',')
                .map((code: string) => code.trim())
                .filter(Boolean),
              groups: []
            };
          }

          // Process group information (whether it's a new customization or additional group)
          if (currentCustomization && row['Tên nhóm']) {
            // Get dish codes from Excel
            const dishCodes = ((row['Mã món theo nhóm'] as string) || '').split(',')
              .map((code: string) => code.trim())
              .filter(Boolean);
            
            // Map dish codes to full dish information
            const dishCodesWithInfo: DishInfo[] = dishCodes.map((code: string) => {
              const dish = dishMap.get(code);
              if (!dish) {
                console.warn(`Dish with code ${code} not found`);
                return {
                  code: code,
                  name: 'Unknown',
                  price: 0,
                  isActive: false
                };
              }
              return {
                code: dish.code,
                name: dish.name,
                price: dish.price,
                isActive: dish.isActive || false
              };
            });

            // Add group to current customization
            currentCustomization.groups.push({
              groupName: row['Tên nhóm'] as string,
              isRequired: row['Yêu cầu chọn'] === 'Có' || row['Yêu cầu chọn'] === true || row['Yêu cầu chọn'] === 1,
              limit: row['Giới hạn chọn'] === '' || row['Giới hạn chọn'] === undefined ? 1 : Number(row['Giới hạn chọn']),
              dishCodes: dishCodesWithInfo
            });
          }
        }

        // Don't forget to add the last customization
        if (currentCustomization) {
          customizations.push(currentCustomization);
        }

        resolve(customizations);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = (error) => reject(error);
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Generate a sample Excel template for customization import
 * @returns Workbook object that can be used to download a template
 */
export const generateCustomizationExcelTemplate = (): XLSX.WorkBook => {
  const worksheet = XLSX.utils.aoa_to_sheet([
    ['Tên', 'Thành phố', 'Mã món áp dụng', 'Tên nhóm', 'Yêu cầu chọn', 'Giới hạn chọn', 'Mã món theo nhóm'],
    ['CB2', 'Hà Nội', 'CB2', 'BEAN', 'Có', '1', 'ETHIOPIA-CB,KENYA-CB'],
    ['', '', '', 'ANOTHER', '', '0', 'ITEM-NLIZ,ITEM-NBQM'],
    ['', '', '', 'TAKE AWAY', '', '0', 'ITEM-NBQM']
  ]);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Mẫu');
  return workbook;
};

/**
 * Download Excel template for customization import
 */
export const downloadCustomizationExcelTemplate = () => {
  const workbook = generateCustomizationExcelTemplate();
  XLSX.writeFile(workbook, 'mau_tuy_chon.xlsx');
}; 